import mainBookSummary from "../../../data/mainBookSummary.json";
import type { OpenPortfolioSummary } from "../../../api/openPortfolioApi";

export const PUBLISHED = mainBookSummary as OpenPortfolioSummary;

export type Part = {
  label: string;
  weight: number;
  tickers: string[];
  tone: string;
};

const PART_OF: Record<string, string> = {
  AMD: "Chips and AI",
  SMH: "Chips and AI",
  TSM: "Chips and AI",
  NVDA: "Chips and AI",
  AVGO: "Chips and AI",
  GOOGL: "Big tech",
  AAPL: "Big tech",
  XOM: "Energy",
  LLY: "Health",
  JPM: "Banking",
  GLD: "Gold",
  BIL: "Cash",
};

const PART_TONES = ["#08150C", "#2F4A36", "#5E7A63", "#93A792", "#C2CDB9", "#D9C9A8", "#E8E1D3"];

/** Groups model portfolio holdings into plain-language parts, largest first. */
export function samplePortfolioParts(summary: OpenPortfolioSummary = PUBLISHED): Part[] {
  const parts = new Map<string, { weight: number; tickers: string[] }>();
  for (const holding of summary.holdings) {
    const label = PART_OF[holding.ticker] ?? "Other";
    const part = parts.get(label) ?? { weight: 0, tickers: [] };
    part.weight += holding.weight;
    part.tickers.push(holding.ticker);
    parts.set(label, part);
  }
  return Array.from(parts.entries())
    .sort((a, b) => b[1].weight - a[1].weight)
    .map(([label, part], index) => ({
      label,
      ...part,
      tone: PART_TONES[index % PART_TONES.length],
    }));
}

export type WeekRecord = {
  total: number;
  up: number;
  down: number;
  since: string | null;
  marks: { weekEnding: string; up: boolean }[];
};

/** Up and down weeks in the static bundled model portfolio series. Direction only, no return figures. */
export function weekRecord(summary: OpenPortfolioSummary = PUBLISHED): WeekRecord {
  const marks = summary.weeks.map((week) => ({
    weekEnding: week.weekEnding,
    up: week.qiReturn >= 0,
  }));
  const up = marks.filter((mark) => mark.up).length;
  return {
    total: marks.length,
    up,
    down: marks.length - up,
    since: summary.inceptionDate,
    marks,
  };
}

export type ResultPoint = { weekEnding: string; portfolio: number; sp: number; drawdown: number };

export type ResultSummary = {
  points: ResultPoint[];
  portfolioTotal: number;
  spTotal: number;
  worstFall: number;
  worstWeek: { weekEnding: string; change: number } | null;
  bestWeek: { weekEnding: string; change: number } | null;
  asOf: string | null;
};

/**
 * Compounds weekly total returns from the static bundled series into growth of $1 for the portfolio and the S&P 500,
 * starting at 1 on inception. Totals, drawdowns and the worst peak-to-low fall are percents.
 */
export function paperResults(summary: OpenPortfolioSummary = PUBLISHED): ResultSummary {
  let portfolio = 1;
  let sp = 1;
  let peak = 1;
  let worstFall = 0;
  let worstWeek: ResultSummary["worstWeek"] = null;
  let bestWeek: ResultSummary["bestWeek"] = null;
  const points: ResultPoint[] = [{ weekEnding: summary.inceptionDate ?? "", portfolio, sp, drawdown: 0 }];

  for (const week of summary.weeks) {
    portfolio *= 1 + week.qiReturn / 100;
    sp *= 1 + week.spReturn / 100;
    peak = Math.max(peak, portfolio);
    const drawdown = portfolio / peak - 1;
    worstFall = Math.min(worstFall, drawdown);
    if (!worstWeek || week.qiReturn < worstWeek.change) {
      worstWeek = { weekEnding: week.weekEnding, change: week.qiReturn };
    }
    if (!bestWeek || week.qiReturn > bestWeek.change) {
      bestWeek = { weekEnding: week.weekEnding, change: week.qiReturn };
    }
    points.push({ weekEnding: week.weekEnding, portfolio, sp, drawdown: drawdown * 100 });
  }

  return {
    points,
    portfolioTotal: (portfolio - 1) * 100,
    spTotal: (sp - 1) * 100,
    worstFall: worstFall * 100,
    worstWeek,
    bestWeek,
    asOf: summary.weeks[summary.weeks.length - 1]?.weekEnding ?? null,
  };
}

/** Splits an amount across the sample parts, rounding each part to whole dollars. */
export function splitAmount(amount: number, parts: Part[] = samplePortfolioParts()) {
  return parts.map((part) => ({ ...part, dollars: Math.round((amount * part.weight) / 100) }));
}

/** Display dollars from the published series; floors to $10 so balances never round up (LAWS 2). */
export function formatModelPortfolioDollarAmount(value: number): string {
  return `$${(Math.floor(value / 10) * 10).toLocaleString("en-US")}`;
}

/** End balance for a starting amount times a growth-of-$1 multiple from {@link paperResults}. */
export function formatPaperGrowthDollars(growthMultiple: number, startAmount: number): string {
  return formatModelPortfolioDollarAmount(growthMultiple * startAmount);
}

export function formatPercent(value: number, digits = 1): string {
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(digits)}%`;
}

export function formatShortDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** Last posted week in the published open-portfolio series. */
export function publishedSeriesEndDate(summary: OpenPortfolioSummary = PUBLISHED): string | null {
  if (summary.weeks.length === 0) return null;
  return summary.weeks[summary.weeks.length - 1].weekEnding;
}

/** Footer stamp for the hero scoreboard card (last posted week, not stale API copy). */
export function formatWeeklyScoreboardStamp(iso: string | null): string {
  if (!iso) return "RECORD STARTING";
  const date = new Date(`${iso}T00:00:00Z`);
  const mon = date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return `WEEKLY · ${mon} ${date.getUTCDate()}`;
}

export function formatMonthYear(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

export function plainWhy(line: string): string {
  return line.replace(/\bbook\b/gi, "portfolio").replace(/\bsleeve\b/gi, "part");
}
